import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DynamoDBClient, AttributeValue } from '@aws-sdk/client-dynamodb';
import { S3Client } from '@aws-sdk/client-s3';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { User } from '../../models/User';
import { Post } from '../../models/Post';
import { FeedService } from '../../utils/FeedService';
import { PostCreator } from './PostCreator';
import { PostCard } from './PostCard';
import { PostSkeleton } from './PostSkeleton';
import { SearchIcon, RefreshIcon, UserIcon, GlobeIcon, PlusIcon, XIcon, ExclamationIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

interface FeedSectionProps {
  ddbClient?: DynamoDBClient;
  s3Client?: S3Client;
  lambdaClient?: LambdaClient;
  currentUser?: User;
  allUsers?: User[];
  isAdmin?: boolean;
}

const EMPTY_ARRAY: any[] = [];

export const FeedSection: React.FC<FeedSectionProps> = ({ ddbClient, s3Client, lambdaClient, currentUser, allUsers, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = window.innerWidth < 768;

  // Separate states for caching
  const [globalPosts, setGlobalPosts] = useState<Post[]>([]);
  const [myPosts, setMyPosts] = useState<Post[]>([]);
  const [searchPosts, setSearchPosts] = useState<Post[]>([]);
  
  const [globalNextKey, setGlobalNextKey] = useState<Record<string, AttributeValue> | undefined>(undefined);
  const [myNextKey, setMyNextKey] = useState<Record<string, AttributeValue> | undefined>(undefined);
  
  const [globalLoaded, setGlobalLoaded] = useState(false);
  const [myLoaded, setMyLoaded] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSearchingLoading, setIsSearchingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState<'GLOBAL' | 'MY_POSTS'>('GLOBAL');
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isImmersiveFeedOpen, setIsImmersiveFeedOpen] = useState(location.pathname === '/feed');
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  
  const feedService = useRef<FeedService | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const loadingRef = useRef(false);
  const lastFetchedViewMode = useRef<'GLOBAL' | 'MY_POSTS' | null>(null);

  // Computed Values
  const posts = isSearching ? searchPosts : (viewMode === 'GLOBAL' ? globalPosts : myPosts);
  const nextKey = viewMode === 'GLOBAL' ? globalNextKey : myNextKey;
  const isInitialLoading = isSearching 
    ? (searchPosts.length === 0 && isSearchingLoading) 
    : (viewMode === 'GLOBAL' ? !globalLoaded : !myLoaded);

  // Unified state setter to handle correct target
  const setPosts = useCallback((updater: Post[] | ((prev: Post[]) => Post[])) => {
    if (isSearching) setSearchPosts(updater);
    else if (viewMode === 'GLOBAL') setGlobalPosts(updater);
    else setMyPosts(updater);
  }, [isSearching, viewMode]);

  // Efficient O(1) user lookup map
  const userMap = React.useMemo(() => {
    const map = new Map<string, User>();
    allUsers?.forEach(u => map.set(u.cognitoUserId, u));
    return map;
  }, [allUsers]);

  const getPoster = useCallback((userId: string) => {
    return userMap.get(userId);
  }, [userMap]);

  const loadFeed = useCallback(async (isRefresh = false) => {
    if (!feedService.current || !currentUser) return;
    if (loadingRef.current && !isRefresh) return;
    
    loadingRef.current = true;
    setIsLoading(true);
    
    // Only show skeletons if we have NO data for this mode OR if it's a forced refresh
    const hasData = viewMode === 'GLOBAL' ? globalPosts.length > 0 : myPosts.length > 0;
    if (isRefresh && !hasData) {
      if (viewMode === 'GLOBAL') setGlobalLoaded(false);
      else setMyLoaded(false);
    }

    setError(null);
    try {
      let result;
      if (viewMode === 'MY_POSTS') {
        result = await feedService.current.getUserPosts(
          currentUser.cognitoUserId,
          10,
          isRefresh ? undefined : myNextKey
        );
      } else {
        // If isRefresh is true, we pass undefined to start from the top
        result = await feedService.current.getGlobalFeed(
          10, 
          isRefresh ? undefined : (viewMode === 'GLOBAL' ? globalNextKey : myNextKey)
        );
      }

      const postIds = result.posts.map(p => p.id);
      const likedPostIds = await feedService.current.getLikeStatuses(postIds, currentUser.cognitoUserId);

      const postsWithLikeStatus = result.posts.map(post => ({
        ...post,
        isLikedByCurrentUser: likedPostIds.has(post.id)
      }));

      const signedPosts = await feedService.current.signUrls(postsWithLikeStatus);

      if (viewMode === 'GLOBAL') {
        setGlobalPosts(prev => isRefresh ? signedPosts : [...prev, ...signedPosts]);
        setGlobalNextKey(result.nextKey);
        setGlobalLoaded(true);
      } else {
        setMyPosts(prev => isRefresh ? signedPosts : [...prev, ...signedPosts]);
        setMyNextKey(result.nextKey);
        setMyLoaded(true);
      }
    } catch (err: any) {
      console.error('Failed to load feed:', err);
      setError(err.message || 'Failed to load family feed. Please check your connection.');
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [currentUser, viewMode, globalNextKey, myNextKey, globalPosts.length, myPosts.length]);

  const handleRefresh = useCallback(async () => {
    // Force a fresh fetch from the top
    await loadFeed(true);
  }, [loadFeed]);

  // Infinite Scroll Observer
  useEffect(() => {
    if (!isImmersiveFeedOpen || isSearching || !nextKey || loadingRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadFeed();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '600px', // Pre-fetch when 600px from bottom
      }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);

    return () => observer.disconnect();
  }, [isImmersiveFeedOpen, isSearching, nextKey, loadFeed]);

  // Determine initial background color from localStorage to prevent white flicker
  const [initialBgColor] = useState(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    return savedDarkMode === 'true' ? '#000000' : '#f1f5f9';
  });

  // Initialize service once clients are ready
  useEffect(() => {
    if (ddbClient && s3Client) {
      feedService.current = new FeedService(ddbClient, s3Client);
    }
  }, [ddbClient, s3Client]);

  // THEME SYNC
  useEffect(() => {
    const html = document.querySelector('html');
    if (!html || !currentUser) return;
    
    const isDark = !!currentUser.darkMode;
    if (isDark) {
      html.classList.add('dark');
      document.body.style.backgroundColor = '#000000'; 
    } else {
      html.classList.remove('dark');
      document.body.style.backgroundColor = '#f1f5f9'; 
    }
  }, [currentUser?.darkMode]);

  // Sync state with URL
  useEffect(() => {
    if (location.pathname === '/feed') {
      setIsImmersiveFeedOpen(true);
    } else {
      setIsImmersiveFeedOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isImmersiveFeedOpen && feedService.current && currentUser) {
      // Only fetch if we haven't successfully loaded this view yet
      const isLoaded = viewMode === 'GLOBAL' ? globalLoaded : myLoaded;
      if (!isLoaded) {
        loadFeed();
      }
    }
  }, [isImmersiveFeedOpen, viewMode, currentUser, globalLoaded, myLoaded, loadFeed]);

  const handleOpenFeed = () => {
    navigate('/feed');
  };

  const handleCloseFeed = () => {
    setIsImmersiveFeedOpen(false);
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 300);
  };

  useEffect(() => {
    const isLocked = !!(isImmersiveFeedOpen || isPostModalOpen || !!postToDelete);
    if (isLocked) {
      if (isMobile) {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.body.setAttribute('data-scroll-pos', scrollY.toString());
      } else {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      if (isImmersiveFeedOpen) document.body.classList.add('immersive-active');
    } else {
      const scrollY = document.body.getAttribute('data-scroll-pos');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('immersive-active');
      
      if (isMobile && scrollY) {
        window.scrollTo(0, parseInt(scrollY));
        document.body.removeAttribute('data-scroll-pos');
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('immersive-active');
    };
  }, [isImmersiveFeedOpen, isPostModalOpen, postToDelete, isMobile]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(async () => {
      if (!query.trim()) {
        setIsSearching(false);
        setSearchPosts([]);
        return;
      }
      
      if (!feedService.current || !currentUser) return;

      setIsSearching(true);
      setIsSearchingLoading(true);
      setSearchPosts([]);
      setError(null);
      try {
        const searchResults = await feedService.current.searchPosts(query);
        const postIds = searchResults.map(p => p.id);
        const likedPostIds = await feedService.current.getLikeStatuses(postIds, currentUser.cognitoUserId);

        const withLikeStatus = searchResults.map(post => ({
          ...post,
          isLikedByCurrentUser: likedPostIds.has(post.id)
        }));

        const signedResults = await feedService.current.signUrls(withLikeStatus);
        setSearchPosts(signedResults);
      } catch (err: any) {
        console.error('Search failed:', err);
        setError('Search failed. Please try again.');
      } finally {
        setIsSearchingLoading(false);
      }
    }, 800);
  }, [currentUser]);

  const handlePostCreated = useCallback(async (content: string, files: File[]) => {
    if (!feedService.current || !currentUser || !lambdaClient) return;
    const newPost = await feedService.current.createPost(content, currentUser.cognitoUserId, files);
    const signedPosts = await feedService.current.signUrls([newPost]);
    
    const newPostFull = { ...signedPosts[0], isLikedByCurrentUser: false };
    setGlobalPosts(prev => [newPostFull, ...prev]);
    if (myLoaded) {
      setMyPosts(prev => [newPostFull, ...prev]);
    }
    
    setIsPostModalOpen(false);

      try {
        await lambdaClient.send(
          new InvokeCommand({
            FunctionName: 'BirthdayEmailV2',
            Payload: new TextEncoder().encode(JSON.stringify({ 
              post: { 
                cognitoUserId: currentUser.cognitoUserId 
              } 
            })),
          })
        );
      } catch (err) {
        console.error('Failed to trigger post notification lambda:', err);
      }
  }, [currentUser, lambdaClient, myLoaded]);

  const handlePostDeleted = useCallback(async () => {
    if (!postToDelete || !feedService.current) return;
    try {
      await feedService.current.deletePost(postToDelete);
      setGlobalPosts(prev => prev.filter(p => p.id !== postToDelete.id));
      setMyPosts(prev => prev.filter(p => p.id !== postToDelete.id));
      setSearchPosts(prev => prev.filter(p => p.id !== postToDelete.id));
      setPostToDelete(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  }, [postToDelete]);

  const handleLikeToggle = useCallback(async (postId: string) => {
    if (!feedService.current || !currentUser) return;
    
    const updatePosts = (prev: Post[]) => prev.map(p => p.id === postId ? {
      ...p,
      isLikedByCurrentUser: !p.isLikedByCurrentUser,
      likeCount: p.likeCount + (p.isLikedByCurrentUser ? -1 : 1)
    } : p);

    // Using functional state updates for all lists
    setGlobalPosts(prev => {
      const post = prev.find(p => p.id === postId);
      if (post) {
        const isLiked = !!post.isLikedByCurrentUser;
        feedService.current?.toggleLike(postId, currentUser.cognitoUserId, isLiked).catch(error => {
          console.error('Failed to toggle like:', error);
          const rollbackPosts = (pList: Post[]) => pList.map(p => p.id === postId ? {
            ...p,
            isLikedByCurrentUser: isLiked,
            likeCount: p.likeCount + (isLiked ? 1 : -1)
          } : p);
          setGlobalPosts(rollbackPosts);
          setMyPosts(rollbackPosts);
          setSearchPosts(rollbackPosts);
        });
      }
      return updatePosts(prev);
    });
    
    setMyPosts(updatePosts);
    setSearchPosts(updatePosts);
  }, [currentUser]);

  const handleCommentChange = useCallback((postId: string, newCount: number, latestComment?: any, metadataUpdate?: Partial<Post>) => {
    const updatePosts = (prev: Post[]) => prev.map(p => {
      if (p.id === postId) {
        let updatedRecentComments = p.recentComments || [];
        if (latestComment) {
          // If a new comment was just added by THIS user, prepend it
          updatedRecentComments = [latestComment, ...updatedRecentComments].slice(0, 2);
        }
        return {
          ...p,
          commentCount: newCount,
          recentComments: updatedRecentComments,
          ...metadataUpdate
        };
      }
      return p;
    });

    setGlobalPosts(updatePosts);
    setMyPosts(updatePosts);
    setSearchPosts(updatePosts);
  }, []);

  const handleSetPostToDelete = useCallback((p: Post) => {
    setPostToDelete(p);
  }, []);

  const lastPostElementRef = useCallback((node: HTMLDivElement) => {
    // This is now handled by the sentinelRef and Infinite Scroll Observer useEffect
  }, []);

  const feedContent = (
    <motion.div 
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.08
          }
        }
      }}
      initial="hidden"
      animate="visible"
      className="flex flex-col w-full relative z-10"
    >
      <AnimatePresence>
        {isInitialLoading ? (
          <motion.div 
            key="skeletons" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            transition={{ duration: 0.2 }} 
            className="space-y-6 w-full"
          >
            {[1, 2, 3].map(i => <PostSkeleton key={i} />)}
          </motion.div>
        ) : (
          <motion.div 
            key="feed-results" 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { 
                opacity: 1, 
                y: 0,
                transition: { 
                  duration: isMobile ? 0.3 : 0.5, 
                  ease: [0.16, 1, 0.3, 1],
                  staggerChildren: 0.08
                } 
              }
            }}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }} 
            className="w-full"
          >
            {error ? (
              <div className="py-20 text-center flex flex-col items-center gap-4 w-full">
                <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                  <ExclamationIcon className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Something went wrong</h3>
                  <p className="text-slate-500 dark:text-slate-400 max-w-xs mx-auto mt-1">{error}</p>
                </div>
                <button onClick={handleRefresh} className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-[background-color,transform] active:scale-95 flex items-center gap-2">
                  <RefreshIcon className="h-4 w-4" /> Try Again
                </button>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 bg-white dark:bg-slate-800/40 rounded-[2.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800 w-full shadow-xl">
                <p className="text-slate-500 dark:text-slate-400 font-medium text-lg px-6">
                  {isSearching ? 'No posts found matching your search.' : 'The feed is quiet... start the conversation!'}
                </p>
                {!isSearching && (
                  <button onClick={() => setIsPostModalOpen(true)} className="mt-4 text-blue-600 dark:text-blue-400 font-bold hover:underline">
                    Create the first post
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6 sm:space-y-8 pb-20 w-full">
                {posts.map((post) => (
                  <motion.div 
                    key={post.id}
                    variants={{
                      hidden: { opacity: 0, y: 15 },
                      visible: { opacity: 1, y: 0 }
                    }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <PostCard 
                      post={post} 
                      s3Client={s3Client as S3Client} 
                      feedService={feedService.current!} 
                      poster={getPoster(post.posterCognitoUserId)} 
                      currentUser={currentUser as User} 
                      allUsers={allUsers || (EMPTY_ARRAY as User[])} 
                      isAdmin={isAdmin} 
                      onLikeToggle={handleLikeToggle} 
                      onDelete={handleSetPostToDelete} 
                      onCommentChange={handleCommentChange} 
                    />
                  </motion.div>
                ))}

                {/* Sentinel for infinite scroll */}
                {nextKey && <div ref={sentinelRef} className="h-10 w-full" />}

                {isLoading && (
                  <div className="space-y-6 mt-6">
                    <PostSkeleton />
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  const immersiveView = (
    <AnimatePresence>
      {isImmersiveFeedOpen && (
        <motion.div 
          id="immersive-feed-view"
          ref={scrollContainerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="fixed inset-0 z-[200] flex flex-col items-center overflow-y-auto overscroll-y-contain"
          style={{ 
            backgroundColor: currentUser ? (currentUser.darkMode ? '#000000' : '#f1f5f9') : initialBgColor,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* STICKY HEADER - Optimized for Safari: Removed blur to stop scroll pulsating */}
          <div className="w-full bg-white dark:bg-slate-900 border-b border-slate-200/50 dark:border-white/10 shrink-0 z-30 sticky top-0 shadow-sm transition-colors duration-500">
            <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex flex-col gap-3 sm:gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button onClick={handleCloseFeed} className="p-2 bg-slate-200/50 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-600 dark:text-slate-100 transition-colors">
                    <XIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <h3 className="text-lg sm:text-2xl font-black tracking-tight dark:text-white whitespace-nowrap">Family Feed</h3>
                    <div className="hidden sm:block px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[10px] font-black uppercase tracking-widest border border-blue-200 dark:border-blue-400/20 shadow-sm">Social</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button onClick={handleRefresh} disabled={isLoading || !currentUser} className="p-2 sm:p-2.5 bg-slate-200/50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-100 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-[background-color,transform] active:scale-95 disabled:opacity-50">
                    <RefreshIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <button onClick={() => setIsPostModalOpen(true)} disabled={!currentUser} className="flex items-center gap-2 px-3 sm:px-6 py-2 sm:py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md active:scale-95 transition-[background-color,transform,shadow] hover:bg-blue-700 disabled:opacity-50">
                    <PlusIcon className="h-5 w-5" /> <span className="hidden sm:inline">New Post</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-1.5 bg-slate-200/30 dark:bg-slate-900/50 p-1 rounded-2xl w-fit shrink-0 border border-slate-200/50 dark:border-white/5 shadow-inner">
                  <button
                    onClick={() => setViewMode('GLOBAL')}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-black transition-[background-color,color,shadow] ${
                      viewMode === 'GLOBAL' 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white'
                    }`}
                  >
                    <GlobeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Global
                  </button>
                  <button
                    onClick={() => setViewMode('MY_POSTS')}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-black transition-[background-color,color,shadow] ${
                      viewMode === 'MY_POSTS' 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' 
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white'
                    }`}
                  >
                    <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> My Posts
                  </button>
                </div>

                <div className="relative flex-grow sm:max-w-xs">
                  <input
                    type="text"
                    placeholder="Search posts..."
                    disabled={!currentUser}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 bg-white/80 dark:bg-slate-900/50 border border-slate-200 dark:border-white/10 rounded-2xl text-base sm:text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-[background-color,border-color,ring] dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 shadow-sm disabled:opacity-50"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <SearchIcon className="absolute left-3 top-2.5 sm:top-3 h-4 w-4 text-slate-400 dark:text-slate-500" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="w-full max-w-2xl px-3 sm:px-6 relative mt-2 sm:mt-4">
            {feedContent}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Launch Button Area - Only visible on main page */}
      <div id="feedLaunchArea" className={`relative h-fit ${window.location.pathname === '/feed' ? 'hidden' : 'block'}`}>
        <div className="p-8 sm:p-12 flex flex-col relative">
          <button 
            onClick={handleOpenFeed}
            className="w-full aspect-[4/3] sm:aspect-[16/6] rounded-[2.5rem] bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-6 sm:p-8 flex flex-col items-center justify-center text-white shadow-xl group relative overflow-hidden transition-all hover:shadow-2xl hover:scale-[1.01] active:scale-[0.99]"
          >
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" />
            <div className="relative z-10 flex flex-col items-center gap-3 sm:gap-4">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-lg">
                <GlobeIcon className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black tracking-tight">Open Family Feed</div>
                <div className="text-blue-100 text-xs sm:text-base font-medium mt-1">Share updates, photos, and keep in touch!</div>
              </div>
              <div className="mt-2 flex items-center gap-2 px-5 py-2 sm:px-6 sm:py-2 bg-white/10 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest backdrop-blur-sm border border-white/20 group-hover:bg-white/20 transition-colors">
                <ExternalLinkIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Launch Immersive View
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* PORTAL: Physically render at body level to escape hidden parents */}
      {createPortal(immersiveView, document.body)}

      {/* New Post Modal */}
      {createPortal(
        <AnimatePresence>
          {isPostModalOpen && currentUser && lambdaClient && s3Client && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[310] flex items-center justify-center p-4" onClick={() => setIsPostModalOpen(false)}>
              <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="bg-white dark:bg-slate-900 w-full max-w-xl rounded-[2rem] overflow-hidden shadow-2xl flex flex-col ring-1 ring-slate-200/50 dark:ring-white/10" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
                  <h3 className="text-xl font-black tracking-tight text-slate-900 dark:text-white uppercase tracking-widest">Create New Post</h3>
                  <button onClick={() => setIsPostModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                    <XIcon className="h-6 w-6 text-slate-500" />
                  </button>
                </div>
                <div className="p-6">
                  <PostCreator user={currentUser} onPostCreated={handlePostCreated} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Unified Delete Speedbump (Modal) */}
      {createPortal(
        <AnimatePresence>
          {postToDelete && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[320] flex items-center justify-center p-4" onClick={() => setPostToDelete(null)}>
              <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] overflow-hidden shadow-2xl border border-slate-200/50 dark:border-white/10" onClick={e => e.stopPropagation()}>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${postToDelete.posterCognitoUserId === currentUser?.cognitoUserId ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                      <ExclamationIcon className={`h-6 w-6 ${postToDelete.posterCognitoUserId === currentUser?.cognitoUserId ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                        {postToDelete.posterCognitoUserId === currentUser?.cognitoUserId ? 'Delete Post?' : 'Admin: Delete Post?'}
                      </h3>
                      <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {postToDelete.posterCognitoUserId === currentUser?.cognitoUserId 
                          ? 'This action cannot be undone and will remove all comments and likes.' 
                          : 'You are deleting someone else\'s post. This action cannot be undone and there could be personal repercussions for removing family content.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={handlePostDeleted} className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-sm">
                      {postToDelete.posterCognitoUserId === currentUser?.cognitoUserId ? 'Yes, Delete Post' : 'Admin Delete'}
                    </button>
                    <button onClick={() => setPostToDelete(null)} className="flex-1 px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">Cancel</button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
